import { Injectable, inject } from '@angular/core';
import { SidebarNavigationItem } from '../models/sidebar-navigation-item.model';
import { SidebarNavigationService } from './sidebar-navigation.service';

interface RankedSidebarItem {
  item: SidebarNavigationItem;
  score: number;
}

@Injectable({
  providedIn: 'root',
})
export class CommandPaletteService {
  private readonly sidebarNavigationService = inject(SidebarNavigationService);

  isOpen = false;
  query = '';
  selectedIndex = 0;
  passengerSearchLoading = false;
  private dynamicPassengerItems: SidebarNavigationItem[] = [];

  open() {
    this.isOpen = true;
    this.query = '';
    this.selectedIndex = 0;
  }

  close() {
    this.isOpen = false;
    this.query = '';
    this.selectedIndex = 0;
    this.passengerSearchLoading = false;
    this.dynamicPassengerItems = [];
  }

  toggle() {
    if (this.isOpen) {
      this.close();
      return;
    }
    this.open();
  }

  updateQuery(query: string) {
    this.query = query;
    this.selectedIndex = 0;
  }

  setPassengerSearchLoading(isLoading: boolean) {
    this.passengerSearchLoading = isLoading;
  }

  setDynamicPassengerItems(items: SidebarNavigationItem[]) {
    this.dynamicPassengerItems = items;
    this.selectedIndex = 0;
  }

  moveSelection(direction: -1 | 1) {
    const items = this.filteredItems;
    if (items.length === 0) {
      this.selectedIndex = 0;
      return;
    }

    const next = this.selectedIndex + direction;
    if (next < 0) {
      this.selectedIndex = items.length - 1;
      return;
    }
    if (next >= items.length) {
      this.selectedIndex = 0;
      return;
    }

    this.selectedIndex = next;
  }

  get activeItem(): SidebarNavigationItem | undefined {
    return this.filteredItems[this.selectedIndex];
  }

  get filteredItems(): SidebarNavigationItem[] {
    const query = this.normalize(this.query);
    const staticItems = this.sidebarNavigationService.getCommandPaletteItems();

    if (!query) {
      return staticItems;
    }

    const rankedStaticItems = staticItems
      .map((item) => this.rankItem(item, query))
      .filter((entry): entry is RankedSidebarItem => entry !== null)
      .sort((a, b) => {
        if (a.score !== b.score) {
          return a.score - b.score;
        }
        return a.item.label.localeCompare(b.item.label);
      });

    // Dynamic passenger items are pre-grouped in triples per passenger.
    return [
      ...rankedStaticItems.map((entry) => entry.item),
      ...this.dynamicPassengerItems,
    ];
  }

  private rankItem(item: SidebarNavigationItem, normalizedQuery: string): RankedSidebarItem | null {
    const searchTexts = this.getNormalizedSearchTexts(item);
    let bestScore: number | null = null;

    for (const searchText of searchTexts) {
      const score = this.rankSearchText(searchText, normalizedQuery);
      if (score === null) {
        continue;
      }

      if (bestScore === null || score < bestScore) {
        bestScore = score;
      }
    }

    if (bestScore === null) {
      return null;
    }

    return { item, score: bestScore };
  }

  private rankSearchText(normalizedSearchText: string, normalizedQuery: string): number | null {
    if (normalizedSearchText.startsWith(normalizedQuery)) {
      return 0;
    }

    const index = normalizedSearchText.indexOf(normalizedQuery);
    if (index >= 0) {
      return 100 + index;
    }

    const fuzzyScore = this.calculateFuzzyScore(normalizedSearchText, normalizedQuery);
    if (fuzzyScore === null) {
      return null;
    }

    return 200 + fuzzyScore;
  }

  private getNormalizedSearchTexts(item: SidebarNavigationItem): string[] {
    const searchTexts = [item.label, ...(item.aliases ?? [])]
      .map((text) => this.normalize(text))
      .filter((text) => text.length > 0);

    return searchTexts.length > 0 ? searchTexts : [this.normalize(item.route)];
  }

  private calculateFuzzyScore(label: string, query: string): number | null {
    let lastMatchIndex = -1;
    let gapPenalty = 0;
    let consecutiveBonus = 0;

    for (const queryChar of query) {
      const matchIndex = label.indexOf(queryChar, lastMatchIndex + 1);
      if (matchIndex === -1) {
        return null;
      }

      if (lastMatchIndex >= 0) {
        const gap = matchIndex - lastMatchIndex - 1;
        gapPenalty += gap;
        if (gap === 0) {
          consecutiveBonus += 1;
        }
      }

      lastMatchIndex = matchIndex;
    }

    const lengthPenalty = Math.max(0, label.length - query.length) * 0.05;
    return gapPenalty + lengthPenalty - consecutiveBonus * 0.5;
  }

  private normalize(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }
}
